import { useCheckout } from 'context/checkout/Checkout';
import React, { useEffect, useState } from 'react';
import { Column } from 'styled/alignment/Column';
import colors from 'theme/colors';
import { CheckCircle, Copy } from 'lucide-react';
import Text from 'components/_UI/Text';
import { Row } from 'styled/alignment/Row';
import styled from 'styled-components';
import Button from 'components/_UI/Button';
import { FullyCentered } from 'styled/alignment/Center';
import breakpoints from 'theme/breakpoints';
import pusherService from 'services/pusher';
import Modal from 'components/_UI/Modal';
import { useCollapse } from 'context/general/Collapse';
import buttonText from 'texts/buttons/buttonText';
import useToast from 'hooks/useToast';
import ExpirationModal from '../commom/ExpirationModal';
import QRCode from 'qrcode.react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import routeParams from 'theme/routeParams';
import paths from 'routes/paths';
import PaymentSuccessModal from './PaymentSuccessModal';


export default function PixCode({ time, onPaymentSuccess, skipSuccessModal }) {
    const { checkout } = useCheckout();
    const { collapse, setCollapse } = useCollapse();
    const { toast } = useToast();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const [timeLeft, setTimeLeft] = useState(time - 5); // 15 minutos em segundos
    const [progress, setProgress] = useState(1); // 100% de progresso
    const [messages, setMessages] = useState([]);
    const [pixCode, setPixCode] = useState();
    const [successModalVisible, setSuccessModalVisible] = useState(false);
    const [expirationModalVisible, setExpirationModalVisible] = useState(false);
    const [modalCopySuccess, setModalCopySuccess] = useState(false)
    const [remainingTime, setRemainingTime] = useState(time - 5)

    useEffect(() => {

        if (timeLeft <= 0) {
            setExpirationModalVisible(true);
            return;
        }

        const interval = setInterval(() => {
            setTimeLeft(prevTime => prevTime - 1);
        }, 1000);

        return () => clearInterval(interval);

    }, [timeLeft]);

    useEffect(() => {
        setProgress(timeLeft / remainingTime);
    }, [timeLeft]);

    useEffect(() => {
        const parsedOrderId = String(checkout?.orderId);

        setPixCode(checkout?.pix?.key);

        if (parsedOrderId) {
            const handleEvent = (data) => {
                setMessages((prevMessages) => [...prevMessages, data.message]);
                if (skipSuccessModal) {
                    onPaymentSuccess?.();
                } else {
                    setSuccessModalVisible(true);
                }
            };

            pusherService.subscribe(`order.${parsedOrderId}`, 'order.status.updated', handleEvent);
        }
    }, [checkout?.orderId]);

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
    };

    function goToWallet() {
        setSuccessModalVisible(false);
        navigate(paths.user.wallet);
    }

    function goToHome() {
        setSuccessModalVisible(false);
        onPaymentSuccess?.();
    }

    async function copyToClipboard() {
        if (navigator?.clipboard?.writeText) {
            try {
                await navigator.clipboard.writeText(pixCode)
                setModalCopySuccess(true)
            } catch (error) {
                copyToClipboardFallback(pixCode)
            }
        }
    }


    function copyToClipboardFallback(value) {
        const input = document.createElement('input');
        input.value = value;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
        setModalCopySuccess(true)
    }

    return (
        <Container>

            {messages.map((el, index) => (
                <Text key={index}>
                    {el}
                </Text>
            ))}

            <Column verticalCenter marginBottom='30px'>
                <Text name='highlight' textAlign={'center'}>
                    Escaneie o QR code e efetue o pagamento.
                </Text>
                <QrCodeCard>
                    <QRCode
                        value={pixCode}
                        size={256}
                        bgColor={"#ffffff"}
                        fgColor={"#000000"}
                        level={"Q"}
                        includeMargin={false}
                    />
                </QrCodeCard>
            </Column>

            <Column>
                <Text name='highlight' textAlign={'center'}>
                    Copie o código e efetue o pagamento.
                </Text>

                <Text textAlign={'center'}>
                    Para efetuar o pagamento, basta copiar o código e pagar através do aplicativo do seu banco ou sua carteira digital.
                </Text>
            </Column>

            <PIXField>
                <PixCodeText>
                    <Text name='highlight' ellipsisAt={'95%'} type='bold' paddingBottom={0}>
                        {pixCode}
                    </Text>
                </PixCodeText>

                <CopyButton onClick={copyToClipboard}>
                    <Copy size={20} color={colors.occasionalPurple} />
                </CopyButton>
            </PIXField>



            <Text ellipsisAt={'90%'} type='bold' color={colors.night} marginTop={'25px'}>
                O tempo para você pagar acaba em:
            </Text>

            <Text name='subtitle' type='bold' color={colors.occasionalPurple} marginTop={'15px'}>
                {formatTime(timeLeft)}
            </Text>

            <ProgressBarContainer>
                <ProgressBarBackground>
                    <ProgressBarFill progress={progress} />
                </ProgressBarBackground>
            </ProgressBarContainer>

            <Button marginTop={'30px'} onClick={copyToClipboard}>
                Copiar código
            </Button>

            {modalCopySuccess && (
                <Modal isOpen={modalCopySuccess} onClose={() => setModalCopySuccess(false)}>
                    <CopySuccessBody>
                        <CopySuccessBadge>
                            <CheckCircle size={22} color={colors.neutralLight} />
                        </CopySuccessBadge>
                        <Text type='bold' paddingBottom={0} color={colors.nightDark} marginTop={'12px'}>
                            Código copiado com sucesso!
                        </Text>
                        <Text name='small' color={colors.night} paddingBottom={0} marginTop={'6px'} textAlign='center'>
                            Cole no aplicativo do seu banco para efetuar o pagamento.
                        </Text>
                        <CopySuccessButton onClick={() => setModalCopySuccess(false)}>
                            {buttonText.understood}
                        </CopySuccessButton>
                    </CopySuccessBody>
                </Modal>
            )}

            {successModalVisible && (
                <PaymentSuccessModal
                    isOpen={successModalVisible}
                    title='Pagamento efetuado'
                    subtitle='Seu pagamento foi processado com sucesso.'
                    onClose={goToHome}
                    onGoToWallet={goToWallet}
                />
            )}

            {expirationModalVisible && <ExpirationModal visible={expirationModalVisible} />}
        </Container>
    );
}

const Container = styled(FullyCentered)`
    flex-direction: column;
    margin-top: 40px;
    padding: 0 30px;

    @media screen and (min-width: ${breakpoints.lg}){
        padding: 0 150px;
    }
`;

const QrCodeCard = styled.div`
    background: ${colors.neutralLight};
    border: 1px solid ${colors.night}11;
    border-radius: 12px;
    padding: 20px;
    margin-top: 20px;
`;

const PIXField = styled.div`
    display: flex;
    align-items: center;
    background: ${colors.night}06;
    border: 1px solid ${colors.night}11;
    border-radius: 10px;
    padding: 12px 14px;
    margin-top: 25px;
    width: 100%;
    gap: 10px;
`;

const PixCodeText = styled.div`
    flex: 1;
    min-width: 0;
    overflow: hidden;
`;

const CopyButton = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 8px;
    background: #F3EAFF;
    cursor: pointer;
    flex-shrink: 0;
    transition: opacity 0.15s ease;

    &:hover {
        opacity: 0.8;
    }
`;

const ProgressBarContainer = styled.div`
    width: 100%;
    margin-top: 20px;
`;

const ProgressBarBackground = styled.div`
    width: 100%;
    height: 6px;
    background-color: ${colors.neutralDark};
    border-radius: 3px;
    overflow: hidden;
`;

const ProgressBarFill = styled.div`
    height: 100%;
    background-color: ${colors.occasionalPurple};
    border-radius: 3px;
    transition: width 1s linear;

    ${({ progress }) => progress && `
        width: ${progress * 100}%;
    `};
`;

const CopySuccessBody = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 8px 0;
`;

const CopySuccessBadge = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: ${colors.successDark};
`;

const CopySuccessButton = styled.button`
    width: 100%;
    max-width: 220px;
    padding: 12px;
    margin-top: 20px;
    border-radius: 10px;
    background: ${colors.occasionalPurple};
    border: none;
    color: ${colors.neutralLight};
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: opacity 0.15s ease;

    &:hover {
        opacity: 0.9;
    }
`;
